import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, X, RefreshCw, LogOut, Search, ClipboardCopy, Package, Lock, Globe, Upload, Loader2 } from "lucide-react";

const KEY_STORE = "cshq_admin_key";

const PERMISSION_LABEL = {
  no_private: "Private — keep private",
  website: "Website",
  social: "Social media",
  website_social: "Website & social",
};
const permissionAllowsWebsite = (p) => p === "website" || p === "website_social";

const STATUSES = ["submitted", "approved", "received", "in_lab", "ready", "returned", "declined"];
const STATUS_LABEL = {
  submitted: "Submitted", approved: "Approved", received: "Received",
  in_lab: "In Lab", ready: "Ready", returned: "Returned", declined: "Declined",
};
const STATUS_COLOR = {
  submitted: "text-[color:var(--chrome-1)] border-[color:var(--line-2)]",
  approved: "text-[color:var(--cyan)] border-[color:var(--cyan)]",
  received: "text-[color:var(--blue)] border-[color:var(--blue)]",
  in_lab: "text-[color:var(--violet)] border-[color:var(--violet)]",
  ready: "text-[color:var(--cyan)] border-[color:var(--cyan)]",
  returned: "text-[color:var(--chrome-2)] border-[color:var(--line-2)]",
  declined: "text-[color:var(--magenta)] border-[color:var(--magenta)]",
};

function Gate({ onAuthed }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!val.trim()) return;
    setBusy(true);
    try {
      await axios.get(`${API}/admin/service-orders`, { headers: { "X-Admin-Key": val.trim() } });
      localStorage.setItem(KEY_STORE, val.trim());
      onAuthed(val.trim());
    } catch (err) {
      toast.error("Invalid admin key");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-gate" className="w-full max-w-md glass holo-border rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-[color:var(--cyan)]" size={26} />
          <h1 className="h-display text-2xl text-chrome">STAFF ACCESS</h1>
        </div>
        <p className="text-sm text-[color:var(--chrome-2)] mb-5">
          Enter the CardScience HQ admin key to manage collectible submissions.
        </p>
        <input
          data-testid="admin-key-input"
          type="password"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Admin key"
          className="w-full bg-transparent border border-[color:var(--line-2)] rounded-xl px-4 py-3.5 text-white focus:border-[color:var(--cyan)] outline-none"
        />
        <button data-testid="admin-key-submit" disabled={busy}
          className="btn-holo w-full rounded-xl py-3.5 mt-4 text-sm disabled:opacity-50">
          {busy ? "CHECKING..." : "ENTER"}
        </button>
      </form>
    </div>
  );
}

function RecommendationPanel({ so, adminKey, onChanged }) {
  const H = { headers: { "X-Admin-Key": adminKey } };
  const rec = so.recommendation;
  const [service, setService] = useState(rec?.service || "");
  const [reason, setReason] = useState(rec?.reason || "");
  const [cost, setCost] = useState(rec ? String((rec.additional_cost_cents || 0) / 100) : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!service.trim() || !reason.trim()) { toast.error("Service and reason are required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/service-orders/${so.reference}/recommendation`, {
        service: service.trim(), reason: reason.trim(),
        additional_cost: Number(String(cost).replace(/[^0-9.]/g, "")) || 0,
      }, H);
      toast.success("ACTION REQUIRED sent to customer");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save recommendation");
    } finally { setBusy(false); }
  };

  const statusColor = rec?.status === "approved" ? "text-[color:var(--cyan)]" : rec?.status === "declined" ? "text-[color:var(--magenta)]" : "text-[color:var(--chrome-2)]";

  return (
    <div data-testid={`recommendation-panel-${so.reference}`} className="rounded-xl border border-[color:var(--line)] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="eyebrow flex items-center gap-2"><ShieldCheck size={13} className="text-[color:var(--cyan)]" /> Action Required — Recommend Extra Work</p>
        {rec && <span className={`text-[10px] font-tech tracking-widest uppercase ${statusColor}`}>{rec.status}{rec.status === "approved" && rec.amount_paid_cents ? ` · $${(rec.amount_paid_cents/100).toFixed(2)}` : ""}</span>}
      </div>
      <input value={service} onChange={(e) => setService(e.target.value)} placeholder="Recommended service (e.g. Card Rehab)"
        className="w-full bg-transparent border border-[color:var(--line-2)] rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)]" />
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why we recommend it" rows={2}
        className="w-full bg-transparent border border-[color:var(--line-2)] rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)]" />
      <div className="flex items-center gap-3">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[color:var(--chrome-3)]">$</span>
          <input value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00"
            className="w-32 bg-transparent border border-[color:var(--line-2)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)]" />
        </div>
        <span className="text-[11px] text-[color:var(--chrome-3)]">Additional cost (0 = no charge). Customer must approve.</span>
        <button data-testid={`rec-save-${so.reference}`} disabled={busy} onClick={save}
          className="btn-holo rounded-lg px-5 py-2.5 text-xs disabled:opacity-50 ml-auto">
          {busy ? "SENDING..." : rec ? "UPDATE" : "SEND TO CUSTOMER"}
        </button>
      </div>
    </div>
  );
}

function GalleryPanel({ so, adminKey, onChanged }) {
  const H = { headers: { "X-Admin-Key": adminKey } };
  const canPublish = permissionAllowsWebsite(so.marketing_permission);
  const [before, setBefore] = useState(so.gallery_before_url || "");
  const [after, setAfter] = useState(so.gallery_after_url || "");
  const [label, setLabel] = useState(so.gallery_label || "");
  const [category, setCategory] = useState(so.gallery_category || "");
  const [published, setPublished] = useState(!!so.gallery_published);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");

  const uploadOne = async (file, setUrl, which) => {
    if (!file) return;
    setUploading(which);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(""); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/service-orders/${so.reference}/gallery`, {
        before_url: before || null, after_url: after || null,
        label: label || null, category: category || null, published,
      }, H);
      toast.success(published ? "Published to public gallery" : "Gallery entry saved");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save gallery entry");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`gallery-panel-${so.reference}`} className="rounded-xl border border-[color:var(--line)] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="eyebrow flex items-center gap-2"><Globe size={13} className="text-[color:var(--cyan)]" /> Public Before/After Gallery</p>
        <span className={`text-[10px] font-tech tracking-widest ${canPublish ? "text-[color:var(--cyan)]" : "text-[color:var(--chrome-3)]"}`}>
          {PERMISSION_LABEL[so.marketing_permission] || "Private"}
        </span>
      </div>
      {!canPublish && (
        <p className="text-xs text-[color:var(--magenta)] flex items-center gap-2">
          <Lock size={13} /> Customer has not granted website permission — this entry cannot be published.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {[["Before", before, setBefore, "before"], ["After", after, setAfter, "after"]].map(([lbl, val, setUrl, which]) => (
          <div key={which}>
            <p className="text-[11px] font-tech tracking-widest text-[color:var(--chrome-3)] mb-1.5">{lbl.toUpperCase()} IMAGE</p>
            {val ? (
              <div className="relative h-24 w-full rounded-lg overflow-hidden border border-[color:var(--line)]">
                <img src={val} alt={lbl} className="h-full w-full object-cover" />
                <button onClick={() => setUrl("")} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center"><X size={11} /></button>
              </div>
            ) : (
              <label className="h-24 w-full rounded-lg border border-dashed border-[color:var(--line-2)] flex flex-col items-center justify-center gap-1 cursor-pointer text-[color:var(--chrome-3)] hover:border-[color:var(--cyan)] hover:text-[color:var(--cyan)] transition-colors">
                {uploading === which ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                <span className="text-[10px] font-tech tracking-wide">{uploading === which ? "UPLOADING" : "UPLOAD"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOne(e.target.files?.[0], setUrl, which)} />
              </label>
            )}
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Public label (e.g. 1999 Holo — surface clean)"
          className="bg-transparent border border-[color:var(--line-2)] rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)]" />
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. Pokémon)"
          className="bg-transparent border border-[color:var(--line-2)] rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)]" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className={`flex items-center gap-2 text-sm ${canPublish ? "text-[color:var(--chrome-1)] cursor-pointer" : "text-[color:var(--chrome-3)] cursor-not-allowed"}`}>
          <input type="checkbox" checked={published} disabled={!canPublish} data-testid={`gallery-publish-${so.reference}`}
            onChange={(e) => setPublished(e.target.checked)} className="accent-[color:var(--cyan)] h-4 w-4" />
          Publish to public gallery
        </label>
        <button data-testid={`gallery-save-${so.reference}`} disabled={busy} onClick={save}
          className="btn-holo rounded-lg px-5 py-2.5 text-xs disabled:opacity-50 ml-auto">
          {busy ? "SAVING..." : "SAVE GALLERY ENTRY"}
        </button>
      </div>
    </div>
  );
}

function Row({ so, adminKey, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const H = { headers: { "X-Admin-Key": adminKey } };

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const approve = () => act(async () => {
    const body = instr.trim() ? { shipping_instructions: instr.trim() } : {};
    const { data } = await axios.post(`${API}/admin/service-orders/${so.reference}/approve`, body, H);
    setInstr(data.shipping_instructions || "");
    toast.success(`Approved ${so.reference} — shipping instructions sent`);
  });
  const decline = () => act(async () => {
    await axios.post(`${API}/admin/service-orders/${so.reference}/decline`, { reason: reason.trim() || undefined }, H);
    toast.success(`Declined ${so.reference}`);
  });
  const setStatus = (status) => act(async () => {
    await axios.post(`${API}/admin/service-orders/${so.reference}/status`, { status }, H);
    toast.success(`${so.reference} → ${STATUS_LABEL[status]}`);
  });

  const items = so.cards || [];

  return (
    <div data-testid={`admin-row-${so.reference}`} className="glass border border-[color:var(--line)] rounded-2xl overflow-hidden">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02]">
        <span className="font-tech text-sm text-[color:var(--cyan)]">{so.reference}</span>
        <span className="text-sm text-white font-semibold">{so.full_name}</span>
        <span className="text-xs text-[color:var(--chrome-3)] uppercase tracking-wide">
          {so.collectible_category}{so.collectible_type ? ` · ${so.collectible_type}` : ""} · {(so.service_type || "").replace("_", " ")}
        </span>
        {so.declared_value ? (
          <span className="text-xs text-[color:var(--chrome-2)]">${Number(so.declared_value).toFixed(0)}</span>
        ) : null}
        <span className={`ml-auto text-[10px] font-tech tracking-widest uppercase px-2.5 py-1 rounded-full border ${STATUS_COLOR[so.status] || ""}`}>
          {STATUS_LABEL[so.status] || so.status}
        </span>
        {permissionAllowsWebsite(so.marketing_permission) ? (
          <span data-testid={`perm-public-${so.reference}`} title={`Publication: ${PERMISSION_LABEL[so.marketing_permission]}`}
            className="text-[color:var(--cyan)] inline-flex items-center gap-1 text-[10px] font-tech tracking-widest">
            <Globe size={13} /> {so.marketing_permission === "website_social" ? "WEB+SOCIAL" : (so.marketing_permission === "website" ? "WEB" : "SOCIAL")}
          </span>
        ) : (
          <span data-testid={`perm-private-${so.reference}`} title="Private — do not publish"
            className="text-[color:var(--chrome-3)] inline-flex items-center gap-1 text-[10px] font-tech tracking-widest">
            <Lock size={13} /> PRIVATE
          </span>
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-[color:var(--line)] space-y-4">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="eyebrow mb-2">Contact</p>
              <p className="text-[color:var(--chrome-1)]">{so.email}</p>
              {so.phone && <p className="text-[color:var(--chrome-2)]">{so.phone}</p>}
              {so.declared_value >= 500 && (
                <p className="text-[color:var(--magenta)] text-xs mt-1">Insurance required ($500+)</p>
              )}
            </div>
            <div>
              <p className="eyebrow mb-2">Collectibles ({items.length})</p>
              <ul className="space-y-1 text-[color:var(--chrome-2)]">
                {items.map((c, i) => (
                  <li key={i}>• {c.description}{c.quantity > 1 ? ` ×${c.quantity}` : ""}</li>
                ))}
              </ul>
              {so.notes && <p className="text-xs text-[color:var(--chrome-3)] mt-2">Notes: {so.notes}</p>}
            </div>
          </div>

          {(so.photos || []).length > 0 && (
            <div>
              <p className="eyebrow mb-2">Photos ({so.photos.length})</p>
              <div className="flex flex-wrap gap-3">
                {so.photos.map((p, i) => (
                  <a key={i} href={p.url} target="_blank" rel="noreferrer" data-testid={`admin-photo-${so.reference}-${i}`}
                    className="h-24 w-24 rounded-xl overflow-hidden border border-[color:var(--line)] block hover:border-[color:var(--cyan)] transition-colors">
                    <img src={p.url} alt={p.filename || "collectible"} className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {so.decline_reason && (
            <div className="rounded-xl border border-[color:var(--magenta)]/40 bg-[color:var(--magenta)]/5 p-3 text-sm text-[color:var(--chrome-1)]">
              Declined: {so.decline_reason}
            </div>
          )}
          {so.shipping_instructions && (
            <div className="rounded-xl border border-[color:var(--cyan)]/40 bg-[color:var(--cyan)]/5 p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="eyebrow">Shipping Instructions (sent)</p>
                <button onClick={() => { navigator.clipboard.writeText(so.shipping_instructions); toast.success("Copied"); }}
                  className="text-[color:var(--cyan)]"><ClipboardCopy size={14} /></button>
              </div>
              <pre className="whitespace-pre-wrap text-xs text-[color:var(--chrome-1)] font-sans leading-relaxed">{so.shipping_instructions}</pre>
            </div>
          )}

          {/* actions */}
          {so.status === "submitted" && (
            <div className="space-y-3">
              <textarea value={instr} onChange={(e) => setInstr(e.target.value)}
                placeholder="Optional: custom shipping instructions (leave blank to auto-generate)"
                className="w-full bg-transparent border border-[color:var(--line-2)] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)] min-h-[70px]" />
              <div className="flex flex-wrap gap-3">
                <button data-testid={`admin-approve-${so.reference}`} disabled={busy} onClick={approve}
                  className="btn-holo rounded-lg px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                  <Check size={15} /> APPROVE & SEND INSTRUCTIONS
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid={`admin-reason-${so.reference}`}
                  placeholder="Reason for declining (optional)"
                  className="flex-1 min-w-[200px] bg-transparent border border-[color:var(--line-2)] rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--magenta)]" />
                <button data-testid={`admin-decline-${so.reference}`} disabled={busy} onClick={decline}
                  className="btn-ghost rounded-lg px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                  <X size={15} /> DECLINE
                </button>
              </div>
            </div>
          )}

          {["approved", "received", "in_lab", "ready"].includes(so.status) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="eyebrow mr-1 flex items-center gap-1"><Package size={13} /> Advance</span>
              {["received", "in_lab", "ready", "returned"].map((s) => (
                <button key={s} data-testid={`admin-status-${so.reference}-${s}`} disabled={busy || so.status === s}
                  onClick={() => setStatus(s)}
                  className="btn-ghost rounded-lg px-3.5 py-2 text-[11px] disabled:opacity-40">
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          )}

          <GalleryPanel so={so} adminKey={adminKey} onChanged={onChanged} />

          <RecommendationPanel so={so} adminKey={adminKey} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (key) => {
    const k = key || adminKey;
    if (!k) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/service-orders`, { headers: { "X-Admin-Key": k } });
      setRows(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(KEY_STORE); setAdminKey(""); }
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { if (adminKey) load(adminKey); }, [adminKey, load]);

  const counts = useMemo(() => {
    const c = {}; rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; }); return c;
  }, [rows]);

  const visible = useMemo(() => rows.filter((r) => {
    if (filter && r.status !== filter) return false;
    if (q) {
      const s = `${r.reference} ${r.full_name} ${r.email} ${r.collectible_type || ""}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, filter, q]);

  if (!adminKey) return <div className="bg-ink"><Gate onAuthed={(k) => setAdminKey(k)} /></div>;

  return (
    <div className="bg-ink min-h-screen pb-24">
      <div className="mx-auto max-w-[1100px] px-5 pt-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-2">Staff Console</p>
            <h1 className="h-display text-3xl sm:text-4xl text-chrome">SUBMISSIONS</h1>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={() => load()} className="btn-ghost rounded-lg px-4 py-2.5 text-xs inline-flex items-center gap-2">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> REFRESH
            </button>
            <button onClick={() => { localStorage.removeItem(KEY_STORE); setAdminKey(""); }}
              className="btn-ghost rounded-lg px-4 py-2.5 text-xs inline-flex items-center gap-2">
              <LogOut size={14} /> SIGN OUT
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="admin-filter-all" onClick={() => setFilter("")}
              className={`text-xs font-tech tracking-wide px-3.5 py-2 rounded-full border ${!filter ? "border-[color:var(--cyan)] text-[color:var(--cyan)]" : "border-[color:var(--line-2)] text-[color:var(--chrome-2)]"}`}>
              ALL ({rows.length})
            </button>
            {STATUSES.map((s) => (
              <button key={s} data-testid={`admin-filter-${s}`} onClick={() => setFilter(s)}
                className={`text-xs font-tech tracking-wide px-3.5 py-2 rounded-full border ${filter === s ? "border-[color:var(--cyan)] text-[color:var(--cyan)]" : "border-[color:var(--line-2)] text-[color:var(--chrome-2)]"}`}>
                {STATUS_LABEL[s].toUpperCase()} ({counts[s] || 0})
              </button>
            ))}
          </div>
          <div className="md:ml-auto relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--chrome-3)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, name, email..."
              data-testid="admin-search"
              className="bg-transparent border border-[color:var(--line-2)] rounded-lg pl-9 pr-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--cyan)] w-full md:w-64" />
          </div>
        </div>

        <div className="space-y-3">
          {visible.length === 0 && (
            <p className="text-sm text-[color:var(--chrome-3)] py-12 text-center">No submissions found.</p>
          )}
          {visible.map((so) => (
            <Row key={so.reference} so={so} adminKey={adminKey} onChanged={() => load()} />
          ))}
        </div>
      </div>
    </div>
  );
}
